import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { checkPermissionStatus, useDebounce } from "helpers/utils";
import { decryptDataFromArray } from "helpers/EncDec";

import serach_icon from "../../assets/images/icon/search-icon.svg";
import view_icon from "../../assets/images/icon/eye.png";
import delete_icon from "../../assets/images/icon/delete.svg";
import edit_icon from "../../assets/images/icon/edit.svg";
import right_arrow from "../../assets/images/icon/right-arrow-table.svg";
import arrow_btn from "../../assets/images/icon/arrow-btn.svg";

import { useTranslation } from "react-i18next";
import {
  DeleteOrganization,
  FetchAllOrganizations,
} from "services/OrganizationServices";
import CreateOrganizationModal from "./CreateOrganizationModal";
import moment from "moment";
import EditOrganizationModal from "./EditOrganizationModal";
import Swal from "sweetalert2";
import RemotePagination from "components/Common/RemotePagination";
import { Spinner } from "reactstrap";
import SearchBar from "components/Common/Searchbar";

const Organization = ({ ...props }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [totalOrgs, setTotalOrgs] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [queryText, setQueryText] = useState("");

  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editOrgDetails, setEditOrgDetails] = useState(null);

  const fetchData = async ({
    _pageNo = pageNo,
    _pageSize = pageSize,
    _queryText = queryText,
  }) => {
    try {
      setLoading(true);

      let reqBody = {
        queryText: _queryText,
        pageNo: _pageNo,
        pageSize: _pageSize,
      };

      let response = await FetchAllOrganizations(reqBody);

      let responseData = decryptDataFromArray(response);

      setAllOrganizations(responseData?.organizationList || []);
      setTotalOrgs(responseData?.total);
      setLoading(false);
    } catch (error) {
      toast.error(t(`Organization.No Organizations Available`));
      setAllOrganizations([]);
      setTotalOrgs(0);
      setLoading(false);
    }
  };

  const handleDeleteOrganization = async orgId => {
    try {
      let response = await DeleteOrganization(orgId);

      let responseMsg =
        response?.data?.message || "Organization Deleted Successfully";

      toast.success(responseMsg);

      fetchData({});
    } catch (error) {
      let errMsg =
        error?.response?.data?.message ||
        error?.response?.message ||
        error?.message ||
        "Error while deleting organization";

      toast.error(errMsg);
    }
  };

  const columns = [
    {
      dataField: "name",
      text: t(`Organization.Name`),
    },
    {
      dataField: "address",
      text: t(`Organization.Address`),
      classes: "white-hidden",
    },
    {
      dataField: "updated_at",
      text: t(`Organization.Last Updated On`),
      classes: "white-hidden",
      formatter: (updated_at, org) => {
        return <>{moment(updated_at).format("DD MMM, YYYY")}</>;
      },
    },
  ];

  if (
    !!checkPermissionStatus("organization", "view") ||
    !!checkPermissionStatus("organization", "edit") ||
    !!checkPermissionStatus("organization", "delete")
  ) {
    columns.push({
      dataField: "id",
      text: t(`Organization.Actions`),
      formatter: (orgId, org) => {
        return (
          <div className="d-flex gap-1 blue-icon-gap action-application">
            <div className="action-one small-img">
              {checkPermissionStatus("organization_spoc", "view") ? (
                <>
                  <div
                    className="text-success blue-icon blue-color4"
                    role="button"
                    onClick={() => {
                      props?.history?.push(`/organization/${orgId}`);
                    }}
                  >
                    <img
                      src={view_icon}
                      alt="view_icon"
                      className="view-icon"
                    />
                    <span>{t(`Organization.View Organization`)}</span>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>

            <div className="action-one small-img">
              {checkPermissionStatus("organization", "edit") ? (
                <>
                  <div
                    className="text-success blue-icon blue-color4"
                    role="button"
                    onClick={() => {
                      setEditOrgDetails(org);
                      setEditModal(true);
                    }}
                  >
                    <img src={edit_icon} alt="edit_icon" />
                    <span>{t(`Organization.Edit Organization`)}</span>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>

            <div className="action-one small-img">
              {checkPermissionStatus("organization", "delete") ? (
                <>
                  <div
                    className="text-success blue-icon blue-color3"
                    role="button"
                    onClick={e => {
                      Swal.fire({
                        title: t("Organization.Are you sure?"),
                        text: t(
                          "Organization.You won't be able to revert this"
                        ),
                        icon: "warning",
                        showCancelButton: true,
                        cancelButtonColor: "#d33",
                        cancelButtonText: t("Organization.Cancel"),
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: t("Organization.Delete"),
                        reverseButtons: true,
                      }).then(async result => {
                        if (!result.isConfirmed) {
                          return;
                        }

                        handleDeleteOrganization(orgId);
                      });
                    }}
                  >
                    <img src={delete_icon} alt="delete_icon" />
                    <span>{t(`Organization.Delete Organization`)}</span>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        );
      },
    });
  }

  /**table arrow */
  const tableWrapperRef = useRef(null);

  const handleScroll = direction => {
    if (tableWrapperRef.current) {
      const scrollAmount = 100; // Adjust this value as needed
      tableWrapperRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setTimeout(() => {
      setPageNo(page);
      setPageSize(sizePerPage);
      fetchData({ _pageNo: page, _pageSize: sizePerPage });
    }, 500);
  };

  useEffect(() => {
    const theme = localStorage.getItem("company_theme");
    if (theme) {
      document.body.classList.add(`theme-${theme}`);
    }
  }, []);

  if (!checkPermissionStatus("organization", "view")) {
    return (
      <div className="page-content">
        <h4 className="text-center">{t(`Organization.Access Denied`)}</h4>
      </div>
    );
  }

  return (
    <>
      <div className="page-content departmentpage">
        <Container fluid className="p-0">
          <div className="joblist-container skilllist  applicationcard-table">
            <Row className="mb-3 align-items-center py-2 spece-low-cum2">
              <Col lg="4" md="4" className="Joblist-column view-Org">
                <div className="joblist-container create-vacancy p-0">
                  <div
                    role="button"
                    onClick={() => {
                      if (typeof props?.history?.goBack === "function") {
                        props?.history?.goBack();
                      } else {
                        props?.history?.push("/dashboard");
                      }
                    }}
                  >
                    <h5 className="text-uppercase heading-one spece-low-cum2">
                      <img src={arrow_btn} alt="arrow_btn" />
                      {t(`Organization.Organization List`)}
                    </h5>
                  </div>
                </div>
              </Col>
              <Col lg="8" md="8">
                <div class="single-filter-box skillset-card btn-left d-flex justify-content-end align-items-center gap-2 flex-wraps">
                  <SearchBar
                    onSearch={fetchData}
                    title={t(`Organization.Search organization`)}
                    placeholder={t(`Organization.Search organization`)}
                    query={queryText}
                    setQuery={setQueryText}
                  />

                  {checkPermissionStatus("organization", "create") && (
                    <div
                      role="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setAddModal(true);
                      }}
                    >
                      {t(`Organization.Create Organization`)}
                    </div>
                  )}

                  <div className="tale-text right-panel skilltest">
                    <button
                      className="button left"
                      onClick={() => handleScroll("left")}
                      data-tooltip="Scroll Left"
                    >
                      <img src={right_arrow} alt="right-arrow" />
                    </button>
                    <button
                      className="button right"
                      onClick={() => handleScroll("right")}
                      data-tooltip="Scroll Right"
                    >
                      <img src={right_arrow} alt="right-arrow" />
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <Row>
            <Col lg={12}>
              <div className="outerTab-box-wrapper below-vacancy  skillset-cards-po">
                <div className="timeto-hire-card userlist">
                  {!!loading && (
                    <div className="m-3 p-3 text-center">
                      <Spinner size={"lg"} />{" "}
                      <div>{t(`Organization.Loading`)}</div>
                    </div>
                  )}

                  {!loading && (
                    <>
                      <div className="table-custome-class range-custome">
                        {!!allOrganizations?.length ? (
                          <>
                            <div className="scroll-container">
                              <div
                                className="table-wrapper"
                                ref={tableWrapperRef}
                              >
                                <RemotePagination
                                  keyField="id"
                                  data={allOrganizations}
                                  columns={columns}
                                  page={pageNo}
                                  sizePerPage={pageSize}
                                  totalSize={totalOrgs}
                                  onTableChange={handleTableChange}
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <table className="table table-hover table-bordered">
                              <thead>
                                <tr>
                                  <th>{t(`Organization.Name`)}</th>
                                  <th>{t(`Organization.Address`)}</th>
                                  <th>{t(`Organization.Last Updated On`)}</th>
                                  <th>{t(`Organization.Actions`)}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td colSpan="4" className="text-center">
                                    <div className="no-data data-available">
                                      <img
                                        src="../img/data-not-available.png"
                                        alt="data not availble"
                                      />
                                      <h3>
                                        {t(
                                          `Organization.No Organizations Available`
                                        )}
                                      </h3>
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        <CreateOrganizationModal
          t={t}
          addModal={addModal}
          setAddModal={setAddModal}
          fetchData={fetchData}
        />

        <EditOrganizationModal
          t={t}
          editModal={editModal}
          setEditModal={setEditModal}
          editOrgDetails={editOrgDetails}
          setEditOrgDetails={setEditOrgDetails}
          fetchData={fetchData}
        />
      </div>
    </>
  );
};

export default Organization;
